import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    Box,
    Button,
    Typography,
    useMediaQuery
} from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles'
import { makeStyles } from '@Compat/makeStyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons/faArrowRight';
import { faCalendarCheck } from '@fortawesome/free-solid-svg-icons/faCalendarCheck';
import { motion, useMotionValue, useTransform } from "framer-motion";
import { useHistory } from "react-router-dom";
import { useSiteContent } from "../../SiteContentContext";
import HeroQuickStart from "../HeroQuickStart";
import { recordCta } from "../../marketing";
import { TOUR_SALES_SRCSET, TOUR_SCREENSHOTS } from "../../assets/illustrations";
import { isCalendlyHref, useDemoDrawer } from "../../DemoDrawerContext";
import { IRoutes } from "../../../../routesEnum";
// Each window ships at its CSS max width (-1x) and double that; the
// browser picks via srcSet/sizes so phones and 1x screens skip the big one.
import HeroResumo from "../../assets/HeroResumo.webp";
import HeroResumo1x from "../../assets/HeroResumo-1x.webp";
import HeroIndicadores from "../../assets/HeroIndicadores.webp";
import HeroIndicadores1x from "../../assets/HeroIndicadores-1x.webp";
import HeroPipeline from "../../assets/HeroPipeline.webp";
import HeroPipeline1x from "../../assets/HeroPipeline-1x.webp";
import HeroOportunidades from "../../assets/HeroOportunidades.webp";
import HeroOportunidades1x from "../../assets/HeroOportunidades-1x.webp";
import AmphibiansoftLogo from "../../assets/AmphibiansoftLogo.webp";
import { scrollToSiteSection } from "../../siteSections";
import { siteTokens } from "../../siteTokens";

// Staggered entrance for the hero copy: each child fades up 14px with a
// 90ms delay between siblings. Visitors land on the page and the
// H1 → subtitle → CTA chain animates in like a director's reveal instead
// of a slab of text appearing all at once.
const HERO_CONTAINER_VARIANTS = {
    hidden: {},
    visible: {
        transition: {
            staggerChildren: 0.09,
            delayChildren: 0.05
        }
    }
};
const HERO_ITEM_VARIANTS = {
    hidden: { opacity: 0, y: 14 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } }
};
// The headline group holds the hero <h1> — the page's LCP element. Unlike the
// other hero items it must NOT start at opacity:0: hiding the largest text
// behind the entrance fade pushes LCP out by the stagger + fade duration.
// Keep it painted from the first frame so it's an LCP candidate the moment
// React mounts; a small transform-only slide still gives it a subtle entrance.
const HERO_HEADLINE_VARIANTS = {
    hidden: { opacity: 1, y: 14 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } }
};

// Real depth-sorted "other windows" — the same assets and rest geometry
// the prototype itself uses (percent/vw-based left/right/top + vw-capped
// width), just ported from its GSAP/CSS scene into this codebase's own
// Framer Motion + makeStyles stack. Two tiers:
//   - far: small, blurred at rest, fade out early
//   - mid: larger, bleed off the left/right edges, exit sideways
// Rest positions and sizes are adapted from the prototype's own CSS, not
// copied verbatim: the prototype's copy (short PT phrases, ~17ch H1 /
// 42ch lead) is noticeably terser than ours (kept as-is, per direction —
// not shortened to match), so at the prototype's own geometry the windows
// collided with our longer, taller copy block. These are pushed further
// toward the corners/lower to clear it while keeping the same "depth
// field around a centered stage" shape.
// The cinematic scroll scales the windows up (far ×1.5, mid ×2.1, main ×1.8 —
// the useTransform ranges below use these). `sizes` must include that, or the
// browser picks the 1x file for the unscaled width and it gets blown up soft.
const FAR_SCALE = 1.5;
const MID_SCALE = 2.1;
const MAIN_SCALE = 1.8;
const scaledSizes = (width: string, scale: number) => `calc(${width} * ${scale})`;

const FAR_WINDOWS = [
    { key: "resumo", src: HeroResumo, srcSet: `${HeroResumo1x} 300w, ${HeroResumo} 600w`, label: "Resumo", style: { left: "4vw", top: "2%", width: "min(22vw, 300px)" } },
    { key: "kpi", src: HeroIndicadores, srcSet: `${HeroIndicadores1x} 210w, ${HeroIndicadores} 420w`, label: "Indicadores", style: { right: "4vw", top: "4%", width: "min(16vw, 210px)" } }
] as const;
const MID_WINDOWS = [
    {
        key: "pipeline",
        src: HeroPipeline,
        srcSet: `${HeroPipeline1x} 340w, ${HeroPipeline} 680w`,
        label: "Pipeline",
        style: { left: "-6vw", top: "50%", width: "min(22vw, 340px)" },
        tilt: "rotateY(22deg) rotateZ(-3deg)",
        exitXPercent: -70,
        exitYPercent: -26
    },
    {
        key: "oportunidades",
        src: HeroOportunidades,
        srcSet: `${HeroOportunidades1x} 340w, ${HeroOportunidades} 680w`,
        label: "Oportunidades",
        style: { right: "-6vw", top: "56%", width: "min(22vw, 340px)" },
        tilt: "rotateY(-22deg) rotateZ(3deg)",
        exitXPercent: 70,
        exitYPercent: -12
    }
] as const;

// How far (as a viewport multiple) the hero's cinematic scroll track
// extends beyond the pinned 100vh stage. The prototype uses 3; this stops
// a bit short (this team has already learned — see VerticalScroller's
// section-order comment — that visitors bounce when the payoff content
// sits too deep below an oversized intro). The scroll-linked values below
// are normalized fractions of this track regardless of its exact length.
const STAGE_TRACK_VIEWPORT_MULTIPLE = 2.4;

// Finds the nearest scrollable ancestor's scroll position indirectly by
// reading the track's own bounding rect — this works whether the page's
// real scroll container is `window` or a custom `overflow:auto` div (as
// VerticalScroller uses), since getBoundingClientRect() is always
// viewport-relative regardless of which element actually owns the
// scrollbar. `scroll` doesn't bubble, but listening on `window` with
// `capture:true` still receives it during the capture phase from any
// descendant scrollable element, so no reference to VerticalScroller's
// internal scroll container is needed here.
const useStageProgress = (trackRef: React.RefObject<HTMLDivElement>, enabled: boolean) => {
    const progress = useMotionValue(0);

    useEffect(() => {
        if (!enabled) {
            progress.set(0);
            return;
        }
        const track = trackRef.current;
        if (!track) {
            return;
        }

        let frame = 0;
        const read = () => {
            frame = 0;
            const rect = track.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const total = rect.height - viewportHeight;
            const scrolled = total > 0 ? Math.min(Math.max(-rect.top, 0), total) : 0;
            progress.set(total > 0 ? scrolled / total : 0);
        };
        const schedule = () => {
            if (!frame) {
                frame = window.requestAnimationFrame(read);
            }
        };

        window.addEventListener("scroll", schedule, { passive: true, capture: true });
        window.addEventListener("resize", schedule);
        read();

        return () => {
            window.removeEventListener("scroll", schedule, { capture: true } as EventListenerOptions);
            window.removeEventListener("resize", schedule);
            if (frame) {
                window.cancelAnimationFrame(frame);
            }
        };
    }, [trackRef, enabled, progress]);

    return progress;
};

// Below this width — or under reduced motion — the scene is a static
// composition: no pin, no scroll-scrub, just the dashboard at full width
// with the copy above it. Matches the prototype's own compound query
// (`(max-width: 860px), (prefers-reduced-motion: reduce)`) but on this
// site's own `md` (900px) breakpoint convention.
const STATIC_QUERY = "@media (max-width: 899.95px), (prefers-reduced-motion: reduce)";
// Short laptop screens (1366×768, 1440×900) in the cinematic layout: the copy
// block runs into the dashboard's rest position, so the copy tightens and the
// dashboard rests lower. Placed before STATIC_QUERY in each style so the
// static (mobile / reduced-motion) layout still wins.
const SHORT_CINEMATIC_QUERY = "@media (max-height: 920px) and (min-width: 900px)";

const useStyles = makeStyles((theme: Theme) => {
    const tk = siteTokens(theme);
    return {
    root: {
        position: "relative",
        width: "100%",
        minHeight: "100vh",
        background: tk.page,
        [theme.breakpoints.down("sm")]: {
            minHeight: "auto"
        }
    },
    // Cinematic scroll track (desktop only — the STATIC_QUERY fallback
    // below collapses this back to auto-height, no pin, no scroll-scrub).
    stageTrack: {
        position: "relative",
        width: "100%",
        height: `${STAGE_TRACK_VIEWPORT_MULTIPLE * 100}vh`,
        [STATIC_QUERY]: {
            height: "auto"
        }
    },
    stageView: {
        position: "sticky",
        // Same AppBar-clearance constant VerticalScroller's own
        // `[data-site-section]` rule uses (theme.spacing(10)) — without
        // it, "top: 0" sticks the stage flush against the viewport edge
        // and the fixed header overlaps/covers its top slice.
        top: theme.spacing(10),
        height: `calc(100vh - ${theme.spacing(10)})`,
        overflow: "hidden",
        isolation: "isolate",
        [STATIC_QUERY]: {
            position: "static",
            height: "auto",
            overflow: "visible",
            padding: theme.spacing(20, 0, 8),
            display: "block",
            [theme.breakpoints.down("sm")]: {
                paddingTop: theme.spacing(14)
            }
        }
    },
    // Dark mode only: a soft cobalt glow adds depth against the near-black
    // canvas. In light mode the same tint painted a visibly different
    // (cooler, mint-tinted) patch behind the scene instead of the flat
    // warm-neutral background every other section uses — so light mode
    // renders no glow at all rather than a mismatched wash.
    stageGlow: {
        position: "absolute",
        inset: "-10% -20% 20%",
        zIndex: 0,
        pointerEvents: "none",
        background: theme.palette.mode === "dark"
            ? `radial-gradient(ellipse at 50% 38%, ${tk.accentA(0.16)}, transparent 62%)`
            : "none"
    },
    // Persistent centered mark behind the whole cinematic scroll track.
    // The scroll-scrubbed windows (far/mid/main) grow, move and fade at
    // different points along the ~240vh track, and for stretches of that
    // scroll range none of them cover the sticky viewport — visitors saw
    // a bare background there. Sitting at z-index 0 (same level as
    // stageGlow, painted after it) keeps this below every window
    // (z-index 1/2/5) and the copy (z-index 4), so it only shows through
    // the gaps rather than fighting them for attention.
    stageWatermark: {
        position: "absolute",
        inset: 0,
        zIndex: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        pointerEvents: "none",
        opacity: theme.palette.mode === "dark" ? 0.14 : 0.08,
        [STATIC_QUERY]: {
            display: "none"
        }
    },
    stageWatermarkImg: {
        width: "min(28vw, 320px)",
        height: "auto"
    },
    stageScene: {
        position: "absolute",
        inset: 0,
        perspective: 1400,
        perspectiveOrigin: "50% 46%",
        pointerEvents: "none",
        // At rest this wrapper being position:absolute is exactly what
        // lets its children stack purely by z-index in the cinematic
        // layout. In the static fallback it would instead pull winMain
        // (itself switched to position:relative below) out of the normal
        // flow entirely, defeating the DOM-order fix that puts the H1
        // before the dashboard image — so it needs to join the flow too.
        [STATIC_QUERY]: {
            position: "relative",
            inset: "auto",
            marginTop: theme.spacing(5.75)
        }
    },
    win: {
        position: "absolute",
        transformStyle: "preserve-3d"
    },
    winFar: {
        opacity: 0.5,
        filter: "blur(2px)",
        zIndex: 1,
        [STATIC_QUERY]: {
            display: "none"
        }
    },
    winMid: {
        zIndex: 2,
        [STATIC_QUERY]: {
            display: "none"
        }
    },
    winMain: {
        left: "50%",
        // Pushed down from the prototype's own 52% — our taller copy block
        // (see stageCopy's comment) needs the dashboard's rest position
        // clear of its bottom edge (secondary CTA link + microcopy line),
        // not just the H1/lead the prototype's own shorter copy has.
        top: "88%",
        width: "min(58vw, 860px)",
        zIndex: 5,
        transform: "translateX(-50%)",
        [SHORT_CINEMATIC_QUERY]: {
            top: "95%"
        },
        [STATIC_QUERY]: {
            position: "relative",
            left: "auto",
            top: "auto",
            width: "100%",
            maxWidth: 900,
            margin: "0 auto",
            transform: "none"
        }
    },
    winInner: {
        position: "relative",
        borderRadius: 16,
        overflow: "hidden",
        border: `1px solid ${tk.inkA(tk.dark ? 0.16 : 0.1)}`,
        // Warm off-white, not pure white — these are scattered, tilted,
        // semi-transparent mockups on an open warm-gray canvas (unlike the
        // full-size rectangular cards elsewhere on the site), so a stark
        // #ffffff read as a jarring white blotch instead of a window that
        // belongs on the page. Still distinct enough (with the border +
        // shadow) to read as chrome.
        //
        // The mark sits as a centered background layer underneath the
        // screenshot <img> — on a slow connection (or the brief gap before
        // the real screenshot paints) visitors see the brand mark instead of
        // a flat, unbranded rectangle. Once the image loads it fully covers
        // this layer since winImg is opaque.
        backgroundImage: `url(${AmphibiansoftLogo})`,
        backgroundRepeat: "no-repeat",
        backgroundPosition: "center",
        backgroundSize: "56px",
        backgroundColor: tk.surface,
        boxShadow: `0 30px 80px ${tk.accentDeepA(0.35)}`
    },
    winBar: {
        display: "flex",
        alignItems: "center",
        gap: 9,
        padding: theme.spacing(0.9, 1.75),
        fontSize: "0.75rem",
        letterSpacing: -0.1,
        color: tk.inkA(tk.dark ? 0.68 : 0.6),
        background: tk.inkA(tk.dark ? 0.04 : 0.03),
        borderBottom: `1px solid ${tk.inkA(tk.dark ? 0.1 : 0.06)}`
    },
    winBarDot: {
        width: 6,
        height: 6,
        borderRadius: "50%",
        background: theme.palette.primary.main,
        flexShrink: 0
    },
    winImg: {
        display: "block",
        width: "100%",
        height: "auto"
    },
    // The copy owns the top of the frame, above the far/mid windows and
    // below the dashboard (z-index 5) that passes over it as the scene
    // plays — at rest nothing overlaps, so the copy is always legible on
    // first paint.
    stageCopy: {
        position: "absolute",
        inset: "clamp(96px, 15vh, 168px) 0 auto",
        zIndex: 4,
        textAlign: "center",
        padding: theme.spacing(0, 4),
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: theme.spacing(2.75),
        // Kept deliberately narrower than the stage itself — our own
        // copy (kept as-is, not shortened to the prototype's terser
        // Portuguese) runs longer than the prototype's, so without a
        // width cap it spans wide enough to collide with the far/mid
        // windows resting near the left/right edges. A narrow centered
        // column leaves both sides clear, same as the prototype's own
        // 17ch/42ch-capped copy does for its shorter text. 46vw stays inside
        // the gap between the mid windows (≈22%–75% of the stage); 640 px lets
        // the H1 sit on two or three lines instead of four, so the block is
        // short enough that the rising dashboard no longer covers "try now"
        // on a 1366×768 laptop.
        maxWidth: "min(640px, 46vw)",
        margin: "0 auto",
        [SHORT_CINEMATIC_QUERY]: {
            inset: "clamp(88px, 12vh, 120px) 0 auto",
            gap: theme.spacing(2)
        },
        [STATIC_QUERY]: {
            position: "static",
            maxWidth: "100%",
            padding: theme.spacing(0, 3)
        },
        [theme.breakpoints.down("sm")]: {
            padding: theme.spacing(0, 2)
        }
    },
    headlineGroup: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(3),
        alignItems: "center"
    },
    title: {
        fontWeight: 800,
        lineHeight: 1.05,
        letterSpacing: -1,
        // Capped lower than the split-screen layout's old 5.2rem — this
        // copy now sits in a narrow centered column (not a half-width
        // grid track), and our own title/subtitle run longer than the
        // prototype's terser Portuguese, so the column needs a smaller
        // ceiling to keep wrapping short enough to clear the side windows.
        fontSize: "clamp(2.2rem, 4.4vw, 3.6rem)",
        textWrap: "balance",
        [SHORT_CINEMATIC_QUERY]: {
            fontSize: "clamp(2.2rem, 3.4vw, 3rem)"
        },
        color: tk.ink
    },
    slogan: {
        fontSize: "clamp(1.05rem, 1.5vw, 1.3rem)",
        lineHeight: 1.5,
        fontWeight: 400,
        color: tk.inkA(tk.dark ? 0.85 : 0.82)
    },
    microCopy: {
        marginTop: theme.spacing(0.5),
        fontSize: "0.85rem",
        fontWeight: 500,
        color: tk.dark ? tk.inkA(0.6) : tk.inkSoft
    },
    ctaGroup: {
        display: "flex",
        flexDirection: "row",
        gap: theme.spacing(4.5),
        alignItems: "center",
        justifyContent: "center",
        flexWrap: "wrap",
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column",
            width: "100%",
            gap: theme.spacing(1.5)
        }
    },
    primaryCta: {
        "&&": {
            position: "relative",
            overflow: "hidden",
            padding: theme.spacing(1.6, 4.5),
            borderRadius: 999,
            fontWeight: 700,
            fontSize: "1.05rem",
            textTransform: "none",
            border: "1.5px solid transparent",
            color: tk.onAccent,
            background: tk.gradient(135),
            boxShadow: `0 18px 38px ${tk.accentA(0.36)}`,
            transition: "transform 200ms ease, box-shadow 200ms ease",
            "&::before": {
                content: "''",
                position: "absolute",
                top: 0,
                left: "-150%",
                width: "75%",
                height: "100%",
                background: "linear-gradient(120deg, transparent 0%, rgba(255,255,255,0.45) 50%, transparent 100%)",
                transform: "skewX(-22deg)",
                transition: "left 700ms ease",
                pointerEvents: "none"
            },
            "&:hover": {
                background: tk.gradient(135),
                transform: "translateY(-2px)",
                boxShadow: `0 26px 52px ${tk.accentA(0.48)}`
            },
            "&:hover::before": {
                left: "150%"
            }
        },
        [theme.breakpoints.down("sm")]: {
            width: "100%"
        }
    },
    secondaryCta: {
        "@keyframes heroArrowNudge": {
            "0%, 100%": { transform: "translateX(0)" },
            "50%": { transform: "translateX(4px)" }
        },
        padding: theme.spacing(1.5, 3.5),
        borderRadius: 999,
        fontWeight: 700,
        fontSize: "1.05rem",
        "& .MuiButton-endIcon, & svg": {
            animation: "$heroArrowNudge 1.8s ease-in-out infinite"
        },
        textTransform: "none",
        backgroundColor: "transparent",
        border: "none",
        color: tk.accent,
        "&:hover": {
            backgroundColor: tk.accentA(0.06)
        },
        [theme.breakpoints.down("sm")]: {
            width: "100%"
        }
    }
    };
});

const HeroSection: React.FC = () => {
    const { content, locale } = useSiteContent();
    const heroContent = content.hero;
    const classes = useStyles();
    const history = useHistory();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
    // Read once — this only needs to gate whether the scroll-linked
    // mechanism is armed at all, not track live changes; a visitor who
    // flips the OS setting mid-session gets the new behaviour on their
    // next visit, same tradeoff VerticalScroller's own reduced-motion
    // handling already makes.
    const [prefersReducedMotion] = useState(
        () => typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
    const cinematicEnabled = isDesktop && !prefersReducedMotion;

    const trackRef = useRef<HTMLDivElement>(null);
    const progress = useStageProgress(trackRef, cinematicEnabled);

    // One normalized timeline (0→1), read as a storyboard — same fractions
    // the prototype's own GSAP timeline uses, just driven by Framer Motion
    // here. The copy steps aside first; the scene is what's left.
    const copyOpacity = useTransform(progress, [0, 0.34], [1, 0]);
    const copyScale = useTransform(progress, [0, 0.34], [1, 0.94]);
    const copyY = useTransform(progress, [0, 0.34], ["0%", "-16%"]);

    // Far windows: small, slow, gone before the middle.
    const farOpacity = useTransform(progress, [0, 0.72], [1, 0]);
    const farScale = useTransform(progress, [0, 0.72], [1, FAR_SCALE]);
    const farY = useTransform(progress, [0, 0.72], ["0%", "-40%"]);

    // Mid windows pass the camera on either side and leave sideways.
    const midOpacity = useTransform(progress, [0.04, 0.92], [1, 0]);
    const midScale = useTransform(progress, [0.04, 0.92], [1, MID_SCALE]);
    const pipelineX = useTransform(progress, [0.04, 0.92], ["0%", `${MID_WINDOWS[0].exitXPercent}%`]);
    const pipelineY = useTransform(progress, [0.04, 0.92], ["0%", `${MID_WINDOWS[0].exitYPercent}%`]);
    const oportunidadesX = useTransform(progress, [0.04, 0.92], ["0%", `${MID_WINDOWS[1].exitXPercent}%`]);
    const oportunidadesY = useTransform(progress, [0.04, 0.92], ["0%", `${MID_WINDOWS[1].exitYPercent}%`]);

    // The dashboard grows and drifts down while the camera closes on it,
    // then accelerates up and leaves through the top of the frame — the
    // sticky stage's own `overflow: hidden` is what clips it. Opacity only
    // drops right at the very end, to catch any sliver left behind.
    const mainScale = useTransform(progress, [0, 0.6, 1], [1, 1.6, MAIN_SCALE]);
    const mainY = useTransform(progress, [0, 0.6, 1], ["0%", "8%", "-240%"]);
    const mainOpacity = useTransform(progress, [0.92, 1], [1, 0]);

    const farStyle = cinematicEnabled ? { opacity: farOpacity, scale: farScale, y: farY } : undefined;
    const pipelineStyle = cinematicEnabled
        ? { opacity: midOpacity, scale: midScale, x: pipelineX, y: pipelineY }
        : undefined;
    const oportunidadesStyle = cinematicEnabled
        ? { opacity: midOpacity, scale: midScale, x: oportunidadesX, y: oportunidadesY }
        : undefined;
    // `x: "-50%"` has to ride along with the animated values here, not live
    // in the CSS class's `transform: translateX(-50%)` — once Framer Motion
    // renders ANY of scale/y/opacity as an inline style, it generates its
    // own `transform` from the motion values it knows about and that inline
    // style wins over the class's `transform`, silently dropping the
    // horizontal centering the moment the scroll animation starts (the
    // window drifts off to the right as it scales up).
    const mainStyle = cinematicEnabled ? { x: "-50%", scale: mainScale, y: mainY, opacity: mainOpacity } : undefined;
    const copyStyle = cinematicEnabled ? { opacity: copyOpacity, scale: copyScale, y: copyY } : undefined;

    const isSubscriptionCta = useCallback(
        (cta?: { id?: string; href?: string; label?: string }) => {
            if (!cta) {
                return false;
            }
            const id = cta.id?.toLowerCase() ?? "";
            const href = cta.href?.toLowerCase() ?? "";
            const label = cta.label?.toLowerCase() ?? "";
            return id.includes("subscribe") || label.includes("subscribe") || href === "#pricing";
        },
        []
    );

    const resolveCtaHref = useCallback(
        (cta?: { id?: string; href?: string; label?: string }): string | undefined => {
            if (!cta) {
                return undefined;
            }
            return isSubscriptionCta(cta) ? IRoutes.wizard : cta.href;
        },
        [isSubscriptionCta]
    );

    const { open: openDemoDrawer } = useDemoDrawer();
    const handleCtaClick = useCallback(
        (
            ctaId: string,
            href: string | undefined,
            event?: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>
        ) => {
            recordCta(ctaId, { locale, section: "hero", href });

            if (isSubscriptionCta({ id: ctaId, href })) {
                event?.preventDefault();
                history.push(IRoutes.wizard);
                return;
            }

            // Calendly URLs open in the drawer (embedded iframe)
            // instead of a new tab — better flow than losing the
            // visitor to a foreign domain.
            if (isCalendlyHref(href)) {
                event?.preventDefault();
                openDemoDrawer(href);
                return;
            }

            // Hash-style links ("#features", "#pricing", etc.) need to
            // scroll-to-section explicitly instead of letting the
            // browser navigate. Inside the site's scroll container the
            // bare hash would land on /login because the router
            // re-resolves the path.
            if (href && href.startsWith("#")) {
                event?.preventDefault();
                scrollToSiteSection(href.slice(1));
            }
        },
        [history, isSubscriptionCta, locale, openDemoDrawer]
    );

    const heroArt = useMemo(() => ({ alt: heroContent.title ?? "" }), [heroContent.title]);

    return (
        <Box className={classes.root} data-site-section="hero">
            <div ref={trackRef} className={classes.stageTrack}>
                <div className={classes.stageView}>
                    <div className={classes.stageGlow} aria-hidden />
                    <div className={classes.stageWatermark} aria-hidden>
                        <img src={AmphibiansoftLogo} alt="" className={classes.stageWatermarkImg} />
                    </div>

                    <motion.div
                        className={classes.stageCopy}
                        variants={HERO_CONTAINER_VARIANTS}
                        initial="hidden"
                        animate="visible"
                        style={copyStyle}
                    >
                        <motion.div className={classes.headlineGroup} variants={HERO_HEADLINE_VARIANTS}>
                            <Typography variant="h2" component="h1" className={classes.title}>
                                {heroContent.title}
                            </Typography>
                            {heroContent.subtitle ? (
                                <Typography className={classes.slogan} component="p">
                                    {heroContent.subtitle}
                                </Typography>
                            ) : null}
                        </motion.div>
                        <motion.div className={classes.ctaGroup} variants={HERO_ITEM_VARIANTS}>
                            {heroContent.primaryCta ? (
                                <Button
                                    component="a"
                                    href={resolveCtaHref(heroContent.primaryCta) ?? heroContent.primaryCta.href}
                                    onClick={(event) =>
                                        handleCtaClick(heroContent.primaryCta.id, heroContent.primaryCta.href, event)
                                    }
                                    color="primary"
                                    variant="contained"
                                    size="medium"
                                    startIcon={<FontAwesomeIcon icon={faCalendarCheck} />}
                                    className={classes.primaryCta}
                                    target={heroContent.primaryCta.href.startsWith("http") ? "_blank" : undefined}
                                    rel={
                                        heroContent.primaryCta.href.startsWith("http") ? "noopener noreferrer" : undefined
                                    }
                                >
                                    {heroContent.primaryCta.label}
                                </Button>
                            ) : null}
                            {heroContent.secondaryCta ? (
                                <Button
                                    component="a"
                                    href={resolveCtaHref(heroContent.secondaryCta) ?? heroContent.secondaryCta.href}
                                    onClick={(event) =>
                                        handleCtaClick(heroContent.secondaryCta.id, heroContent.secondaryCta.href, event)
                                    }
                                    color="primary"
                                    variant="text"
                                    size="medium"
                                    endIcon={<FontAwesomeIcon icon={faArrowRight} />}
                                    className={classes.secondaryCta}
                                    target={heroContent.secondaryCta.href.startsWith("http") ? "_blank" : undefined}
                                    rel={
                                        heroContent.secondaryCta.href.startsWith("http") ? "noopener noreferrer" : undefined
                                    }
                                >
                                    {heroContent.secondaryCta.label}
                                </Button>
                            ) : null}
                        </motion.div>
                        <motion.div variants={HERO_ITEM_VARIANTS}>
                            <HeroQuickStart strings={heroContent.quickStart} locale={locale} />
                        </motion.div>
                        {heroContent.microCopy ? (
                            <motion.div variants={HERO_ITEM_VARIANTS}>
                                <Typography component="p" className={classes.microCopy}>
                                    {heroContent.microCopy}
                                </Typography>
                            </motion.div>
                        ) : null}
                    </motion.div>

                    {/* Rendered AFTER stageCopy in the DOM on purpose: in the
                        cinematic (desktop) layout everything here is
                        position:absolute, so z-index (not source order)
                        governs the visual stack — main(5) > copy(4) >
                        mid(2) > far(1). In the static/reduced-motion
                        fallback these become normal-flow elements instead,
                        and DOM order is all that's left to put the H1
                        before the dashboard screenshot rather than after it. */}
                    <div className={classes.stageScene} aria-hidden>
                        {FAR_WINDOWS.map((win) => (
                            <motion.div
                                key={win.key}
                                className={`${classes.win} ${classes.winFar}`}
                                style={{ ...win.style, ...farStyle }}
                            >
                                <div className={classes.winInner}>
                                    <div className={classes.winBar}>
                                        <span className={classes.winBarDot} />
                                        {win.label}
                                    </div>
                                    <img src={win.src} srcSet={win.srcSet} sizes={scaledSizes(win.style.width, FAR_SCALE)} alt="" className={classes.winImg} />
                                </div>
                            </motion.div>
                        ))}
                        {MID_WINDOWS.map((win, index) => (
                            <motion.div
                                key={win.key}
                                className={`${classes.win} ${classes.winMid}`}
                                style={{ ...win.style, ...(index === 0 ? pipelineStyle : oportunidadesStyle) }}
                            >
                                <div className={classes.winInner} style={{ transform: win.tilt }}>
                                    <div className={classes.winBar}>
                                        <span className={classes.winBarDot} />
                                        {win.label}
                                    </div>
                                    <img src={win.src} srcSet={win.srcSet} sizes={scaledSizes(win.style.width, MID_SCALE)} alt="" className={classes.winImg} />
                                </div>
                            </motion.div>
                        ))}
                        <motion.div className={`${classes.win} ${classes.winMain}`} style={mainStyle}>
                            <div className={classes.winInner} style={{ transform: cinematicEnabled ? "rotateX(3deg)" : undefined }}>
                                <div className={classes.winBar}>
                                    <span className={classes.winBarDot} />
                                    Fidava · Vendas
                                </div>
                                <img src={TOUR_SCREENSHOTS.sales} srcSet={TOUR_SALES_SRCSET} sizes={scaledSizes("min(58vw, 860px)", MAIN_SCALE)} alt={heroArt.alt} className={classes.winImg} />
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </Box>
    );
};

export default HeroSection;
